/**
 * Centralized hostname and environment configuration.
 */

interface Window {
  BOOTSTRAPPED_DATA?: {
    host?: string;
    standaloneClient?: boolean;
  };
}

const timelyBootstrappedData = ((globalThis as any).window as Window)?.BOOTSTRAPPED_DATA ?? {};

/**
 * Safe `process.env` read for code shared between TIC backend (Node) and the
 * Timely FE bundle (browser). In the browser `process` may be undefined unless
 * the key is explicitly defined in Vite's `define` config — return undefined
 * there so callers fall through to their defaults.
 */
const readProcessEnv = (key: string): string | undefined =>
  typeof process !== 'undefined' ? process.env[key] : undefined;
type AppBackends = 'timely' | 'timelyApi' | 'tic';
type LiveEnvironment = 'staging' | 'production' | 'hydra' | 'phoenix' | 'theory';
export type Environments = 'development' | LiveEnvironment;
type EnvironmentHostnames = {
  [K in Environments]?: string;
};

export const envHostnameMap: Record<AppBackends, EnvironmentHostnames> = {
  /** Timely *webapp* (or when targeting *session*-based API */
  timely: {
    production: 'app.timelyapp.com',
    staging: 'app.betimely.com',
    hydra: 'app.hydra.betimely.com',
    phoenix: 'app.phoenix.betimely.com',
    theory: 'app.theory.betimely.com',
    development: 'app.timelyapp.local:3002',
  },
  /** Timely Bearer token API targets */
  timelyApi: {
    production: 'api.timelyapp.com',
    staging: 'api.betimely.com',
    hydra: 'api.hydra.betimely.com',
    phoenix: 'api.phoenix.betimely.com',
    theory: 'api.theory.betimely.com',
    // TIC backend sets `TIMELY_API_URL` to point at its local/staging Timely Rails.
    // FE bundle has no such override and falls back to prod.
    development: readProcessEnv('TIMELY_API_URL') || 'api.timelyapp.com',
  },
  tic: {
    development: 'localhost:3011',
    production: 'integrations.app.timelyapp.com',
    staging: 'integrations.app.betimely.com',
    // We only have one staging env for now...
    hydra: 'disable',
    phoenix: 'disable',
    theory: 'disable',
  },
} as const;

const toHostname = (urlOrHostname: string): string => {
  const value =
    urlOrHostname.startsWith('http://') || urlOrHostname.startsWith('https://')
      ? urlOrHostname
      : `https://${urlOrHostname}`;

  return new URL(value).hostname;
};

export const getEnvironmentFromUrl = (url: string): Environments => {
  const host = new URL(url).hostname;
  for (const [env, hostname] of Object.entries(envHostnameMap.timely).concat(
    Object.entries(envHostnameMap.timelyApi)
  )) {
    const parsedHostname = toHostname(hostname);
    if (host.includes(parsedHostname)) {
      return env as Environments;
    }
  }
  return 'production'; // Default to production if no match is found
};

/**
 * Identifies the target API environment (not process.env.NODE_ENV).
 *
 * In production deployment, the same bundle is served on every env (prod, staging, hydra...)
 * and the environment is derived from the Rails-injected `bootstrappedData.host`. Tests force
 * `'development'` so they hit MSW-mocked localhost endpoints.
 */
export const getEnvironment = (): Environments => {
  const NODE_ENV = getEnvVar({ envVar: process.env.NODE_ENV, ifUndefinedEnVar: false });

  if (NODE_ENV === 'test') return 'development';

  if (timelyBootstrappedData?.host) {
    return getEnvironmentFromUrl(timelyBootstrappedData.host);
  }

  // TIC web-app running locally:
  const inBrowser = typeof window !== 'undefined';
  const ticLocalEnvs = [envHostnameMap.tic.development];

  if (inBrowser && ticLocalEnvs.includes(window.location.host)) {
    return 'development';
  }

  const timelyApiUrl = readProcessEnv('TIMELY_API_URL');
  const fallbackEnv = timelyApiUrl && getEnvironmentFromUrl(timelyApiUrl);

  return fallbackEnv || (NODE_ENV as Environments) || 'production';
};

export const isTicDisabledForEnvironment = () => {
  const host = timelyBootstrappedData?.host || '';
  const env = getEnvironmentFromUrl(host);
  return envHostnameMap.tic[env] === 'disable';
};

/**
 * Returns `true` if diff generation should be short-circuited.
 * Defaults to `false` when the flag is unset so prod stays enabled by default.
 */
export const isTimelyDiffGenerationDisabled = (): boolean =>
  getEnvVar({
    envVar: process.env.TIMELY_DIFF_GENERATION_DISABLED,
    type: 'boolean',
    default: false,
    ifUndefinedEnVar: false,
  });

/**
 * Returns true if we're in a "live" Timely environment (not dev mode),
 * rendered by spa.html.erb / Rails.
 */
export const getIsLiveTimelyClientSideEnv = () => {
  return !!timelyBootstrappedData.host && !timelyBootstrappedData.standaloneClient;
};

/**
 * JWT expiration time in minutes.
 * @default 60
 */
export const getTicAuthExpirationMinutes = (): number =>
  getEnvVar({
    envVar: process.env.TIC_AUTH_EXPIRATION_MINUTES,
    type: 'number',
    default: 60,
  });

export const getTicApiCredentialsSetting = (): RequestCredentials => {
  if (getIsLiveTimelyClientSideEnv()) return 'include';
  return 'same-origin';
};

export const getTicApiBaseUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();
  switch (env) {
    case 'development':
      return `http://${envHostnameMap.tic[env]}/api`;
    default:
      return `https://${envHostnameMap.tic[env]}/api`;
  }
};

export const getAiServiceApiBaseUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();
  switch (env) {
    case 'staging':
      return `https://63tzsb6no5.execute-api.eu-west-1.amazonaws.com/staging`;
    case 'development':
      return `http://localhost:4444`;
    default:
      return `https://qvuj8lwl4i.execute-api.eu-west-1.amazonaws.com/production`;
  }
};

export const getAgentServiceApiBaseUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();
  switch (env) {
    case 'staging':
      return `https://42rhmfwuf6.execute-api.eu-west-1.amazonaws.com/staging`;
    case 'development':
      return `http://localhost:4444`;
    default:
      return `https://42rhmfwuf6.execute-api.eu-west-1.amazonaws.com/production`;
  }
};

export const getTimelyBaseUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();

  if (env === 'development') {
    return `http://${envHostnameMap.timely.development}`;
  }

  return `https://${envHostnameMap.timely[env]}`;
};

/**
 * Returns the Timely API base URL.
 *
 * TIC backend sets `TIMELY_API_URL` in the ECS task definition (and locally in
 * `apps/integration-cloud/.env`) to point at its target Timely Rails. The FE
 * bundle never reads this — it picks the URL from the env hostname map based
 * on the resolved environment.
 */
export const getTimelyApiBaseUrl = (suggestEnvironment?: Environments): string => {
  const env = suggestEnvironment || getEnvironment();
  return getEnvVar({
    envVar: readProcessEnv('TIMELY_API_URL'),
    type: 'string',
    default: `https://${envHostnameMap.timelyApi[env]}`,
  });
};

/**
 * Target URL that syncs current Timely session -> TIC, returning a TIC JWT back.
 */
export const getTimelySessionUrlForTicJwtExchange = () => {
  // Rails-served FE: use the same-origin Rails /tic/sessions, which avoids a
  // cross-origin call to TIC and reuses the existing Rails session for auth.
  if (!timelyBootstrappedData?.standaloneClient) {
    return `${getTimelyBaseUrl()}/tic/sessions`;
  }

  // For React FE "standalone"/not-session-auth, we hit Timely's API directly.
  return `${getTimelyApiBaseUrl()}/tic/sessions`;
};

/**
 * Returns the Nango Connect host the FE points at when creating browser-side
 * sessions. The public key isn't needed: Nango Connect authenticates via a
 * short-lived session token issued by the TIC backend (which holds the
 * server-side Nango secret key).
 */
export const getNangoServerUrl = (forceEnvironment?: Environments): string => {
  const env = forceEnvironment || getEnvironment();

  const nangoUrl = getEnvVar({
    envVar: readProcessEnv('NANGO_SERVER_URL'),
    type: 'string',
    default:
      env === 'production'
        ? 'https://auth.integrations.timelyapp.com'
        : env === 'staging'
          ? 'https://auth.integrations.betimely.com'
          : env === 'development'
            ? 'http://localhost:3003'
            : undefined,
  });

  if (!nangoUrl) {
    throw new Error('Missing Nango `NANGO_SERVER_URL`');
  }

  return nangoUrl;
};

/**
 * Returns the value of an environment variable, with optional type casting and default value.
 */
type EnvVarType = 'string' | 'boolean' | 'number';
type EnvVarReturn<T extends EnvVarType | undefined, V> = T extends 'boolean'
  ? boolean
  : T extends 'number'
    ? number
    : V extends string
      ? V
      : string;

interface EnvVarConfig<T extends EnvVarType | undefined = undefined, V = string> {
  envVar: V | string | number | boolean | undefined;
  type?: T;
  default?: EnvVarReturn<T, V>;
  ifUndefinedEnVar?: false | 'warn' | 'throw' | 'throwIfNotDefault';
}
export const warnedEnvVars = new Set<string>();

/**
 * Returns the value of an environment variable, with optional type casting and default value.
 * Handles undefined/empty values according to the specified behavior:
 *
 * @param envVar - The environment variable value to process
 * @param type - Optional type casting ('string' | 'boolean' | 'number')
 * @param default - Default value to return if envVar is undefined
 * @param ifUndefinedEnVar - Behavior when envVar is undefined:
 *   - false: Silently continue
 *   - 'warn': Log warning once per unique env var
 *   - 'throw': Always throw error
 *   - 'throwIfNotDefault': Throw only if no default value provided
 *
 * Default behavior:
 *   - Development: 'throwIfNotDefault'
 *   - Other environments: 'warn'
 */
export function getEnvVar<T extends EnvVarType | undefined = undefined, V = string>({
  envVar,
  type,
  default: defaultValue,
  ifUndefinedEnVar = process.env.NODE_ENV === 'development' ? 'throwIfNotDefault' : 'warn',
}: EnvVarConfig<T, V>): EnvVarReturn<T, V> {
  if (envVar === undefined || envVar === null || envVar === '') {
    if (ifUndefinedEnVar === 'throw') {
      throw new Error('getEnvVar:: Environment variable is undefined');
    }

    if (ifUndefinedEnVar === 'throwIfNotDefault' && defaultValue === undefined) {
      throw new Error(
        'getEnvVar:: Environment variable is undefined and no default value provided'
      );
    }

    if (
      ifUndefinedEnVar === 'warn' &&
      defaultValue === undefined &&
      !warnedEnvVars.has(String(envVar))
    ) {
      console.warn('getEnvVar:: Environment variable is undefined');
      warnedEnvVars.add(String(envVar));
    }

    return (defaultValue || false) as EnvVarReturn<T, V>;
  }

  // Auto-detect type if not specified
  const detectedType =
    type ||
    ((): EnvVarType => {
      if (typeof envVar === 'boolean' || envVar === 'true' || envVar === 'false') return 'boolean';
      if (typeof envVar === 'number' || !isNaN(Number(envVar))) return 'number';
      return 'string';
    })();

  try {
    switch (detectedType) {
      case 'boolean':
        if (typeof envVar === 'boolean') return envVar as EnvVarReturn<T, V>;
        return (envVar.toString().toLowerCase() === 'true') as EnvVarReturn<T, V>;
      case 'number': {
        const num = Number(envVar);
        if (isNaN(num)) throw new Error('Invalid number');
        return num as EnvVarReturn<T, V>;
      }
      case 'string':
        return envVar.toString() as EnvVarReturn<T, V>;
      default:
        throw new Error(`Unsupported env var type: ${detectedType}`);
    }
  } catch (error: any) {
    throw new Error(
      `Failed to parse environment variable as ${detectedType}. ` +
        `Value: "${envVar}" (${typeof envVar}). ${error.message}`
    );
  }
}
